import { createApi } from "@reduxjs/toolkit/query/react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { createBaseQuery } from "@/api/base-query";
import { API_ENDPOINTS } from "@/api/config";
import type {
  GetSchedulePublicAvailableDatesInput,
  GetSchedulePublicAvailableSlotsInput,
  GetSchedulePublicBookingDetailsInput,
  GetSchedulePublicBookingLinkInput,
  SchedulePublicCreateBookingInput,
  SchedulePublicReserveSlotInput,
  SchedulePublicAvailableDatesData,
  SchedulePublicAvailableDatesResponse,
  SchedulePublicAvailableSlot,
  SchedulePublicAvailableSlotsData,
  SchedulePublicAvailableSlotsResponse,
  SchedulePublicBookingLink,
  SchedulePublicBookingLinkResponse,
  SchedulePublicHost,
  SchedulePublicLocation,
  SchedulePublicQuestion,
  SchedulePublicBookingData,
  SchedulePublicBookingDetails,
  SchedulePublicBookingDetailsResponse,
  SchedulePublicCancelBookingInput,
  SchedulePublicCancelBookingRequest,
  SchedulePublicCancelBookingData,
  SchedulePublicCancelBookingResult,
  SchedulePublicCancelBookingResponse,
  SchedulePublicCreateBookingResult,
  SchedulePublicCreateBookingResponse,
  SchedulePublicRescheduleBookingInput,
  SchedulePublicRescheduleBookingRequest,
  SchedulePublicRescheduleBookingData,
  SchedulePublicRescheduleBookingResult,
  SchedulePublicRescheduleBookingResponse,
  SchedulePublicSendEmailVerificationInput,
  SchedulePublicSendEmailVerificationRequest,
  SchedulePublicSendEmailVerificationResponse,
  SchedulePublicEmailVerificationData,
  SchedulePublicReserveSlotData,
  SchedulePublicReserveSlotRequest,
  SchedulePublicReserveSlotResponse,
  SchedulePublicWorkspace,
} from "./schedule-public.types";
import { SCHEDULE_PUBLIC_VALIDATION_MESSAGES } from "./schedule-public.types";

const ENDPOINTS = API_ENDPOINTS.scheduling.public;
const publicBaseQuery = createBaseQuery("public");

const createApiError = (
  status: number,
  message: string,
): { error: FetchBaseQueryError } => ({
  error: {
    status,
    data: { message },
  },
});

const trimString = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isPositiveInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0;

const isValidDate = (date: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(date) &&
  !Number.isNaN(Date.parse(`${date}T00:00:00Z`));

const isValidSlotTimestamp = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})?$/.test(
    value,
  );

const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

// Accepts any IANA identifier the runtime recognizes, including non-canonical
// aliases such as "Asia/Calcutta" that the booking link may carry. Falls back to
// a permissive check when Intl timezone validation is unavailable.
const isValidTimezone = (value: string): boolean => {
  if (!value) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return /^[A-Za-z][A-Za-z0-9+_-]*(?:\/[A-Za-z0-9+_-]+)*$/.test(value);
  }
};

const validateTimezone = (value: unknown): string | null => {
  const timezone = trimString(value);
  if (!timezone) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.TIMEZONE_REQUIRED;
  if (!isValidTimezone(timezone)) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_TIMEZONE;
  }
  return null;
};

const normalizeBookingLinkInput = (
  input: GetSchedulePublicBookingLinkInput,
): { publicKey: string } => {
  if (typeof input === "string") return { publicKey: trimString(input) };

  return {
    publicKey: trimString(input.publicKey),
  };
};

const validatePublicKey = (publicKey: string): string | null => {
  if (!publicKey) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.PUBLIC_KEY_REQUIRED;
  if (!/^evt_[A-Za-z0-9_-]+$/.test(publicKey)) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_PUBLIC_KEY;
  }
  return null;
};

const validateSlotsInput = (
  input: GetSchedulePublicAvailableSlotsInput,
): string | null => {
  const publicKeyError = validatePublicKey(trimString(input.publicKey));
  if (publicKeyError) return publicKeyError;

  const date = trimString(input.date);
  if (!date) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.DATE_REQUIRED;
  if (!isValidDate(date)) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_DATE;

  const timezoneError = validateTimezone(input.timezone);
  if (timezoneError) return timezoneError;

  return null;
};

const validateAvailableDatesInput = (
  input: GetSchedulePublicAvailableDatesInput,
): string | null => {
  const publicKeyError = validatePublicKey(trimString(input.publicKey));
  if (publicKeyError) return publicKeyError;

  const from = trimString(input.from);
  if (!from) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.FROM_DATE_REQUIRED;
  if (!isValidDate(from)) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_DATE;

  const to = trimString(input.to);
  if (!to) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.TO_DATE_REQUIRED;
  if (!isValidDate(to)) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_DATE;

  if (to < from) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_DATE_RANGE;

  const timezoneError = validateTimezone(input.timezone);
  if (timezoneError) return timezoneError;

  return null;
};

const validateReserveSlotInput = (
  input: SchedulePublicReserveSlotInput,
): string | null => {
  const publicKeyError = validatePublicKey(trimString(input.publicKey));
  if (publicKeyError) return publicKeyError;

  const date = trimString(input.date);
  if (!date) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.DATE_REQUIRED;
  if (!isValidDate(date)) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_DATE;

  const timezoneError = validateTimezone(input.timezone);
  if (timezoneError) return timezoneError;

  const startTime = trimString(input.startTime);
  if (!startTime) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.START_TIME_REQUIRED;
  if (!isValidSlotTimestamp(startTime)) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_SLOT_TIME;
  }

  const endTime = trimString(input.endTime);
  if (!endTime) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.END_TIME_REQUIRED;
  if (!isValidSlotTimestamp(endTime)) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_SLOT_TIME;
  }

  const inviteeEmail = trimString(input.inviteeEmail);
  if (!inviteeEmail) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVITEE_EMAIL_REQUIRED;
  }
  if (!isValidEmail(inviteeEmail)) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_INVITEE_EMAIL;
  }

  return null;
};

const validateCreateBookingInput = (
  input: SchedulePublicCreateBookingInput,
): string | null => {
  const publicKeyError = validatePublicKey(trimString(input.publicKey));
  if (publicKeyError) return publicKeyError;

  if (!trimString(input.lockKey)) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.LOCK_KEY_REQUIRED;
  }

  const timezoneError = validateTimezone(input.inviteeTimezone);
  if (timezoneError) return timezoneError;

  if (!Array.isArray(input.invitees) || input.invitees.length === 0) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVITEE_REQUIRED;
  }

  const invalidInvitee = input.invitees.find(
    (invitee) => !trimString(invitee.email) || !isValidEmail(trimString(invitee.email)),
  );
  if (invalidInvitee) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_INVITEE_EMAIL;
  }

  const hasVerificationTokenId =
    typeof input.verificationTokenId !== "undefined";
  const hasVerificationCode = trimString(input.verificationCode).length > 0;
  if (hasVerificationTokenId || hasVerificationCode) {
    if (!hasVerificationTokenId) {
      return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.VERIFICATION_TOKEN_ID_REQUIRED;
    }
    if (
      typeof input.verificationTokenId !== "number" ||
      !Number.isInteger(input.verificationTokenId) ||
      input.verificationTokenId < 0
    ) {
      return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_VERIFICATION_TOKEN_ID;
    }
    if (!hasVerificationCode) {
      return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.VERIFICATION_CODE_REQUIRED;
    }
    if (trimString(input.verificationCode).length > 32) {
      return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_VERIFICATION_CODE;
    }
  }

  return null;
};

const validateSendEmailVerificationInput = (
  input: SchedulePublicSendEmailVerificationInput,
): string | null => {
  const publicKeyError = validatePublicKey(trimString(input.publicKey));
  if (publicKeyError) return publicKeyError;

  const email = trimString(input.email);
  if (!email) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVITEE_EMAIL_REQUIRED;
  if (!isValidEmail(email)) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_INVITEE_EMAIL;
  }

  return null;
};

const validateBookingId = (bookingId: string): string | null => {
  if (!bookingId) return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.BOOKING_ID_REQUIRED;
  if (!/^[A-Za-z0-9_-]+$/.test(bookingId)) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_BOOKING_ID;
  }
  return null;
};

const validateRescheduleBookingInput = (
  input: SchedulePublicRescheduleBookingInput,
): string | null => {
  const publicKeyError = validatePublicKey(trimString(input.publicKey));
  if (publicKeyError) return publicKeyError;

  const bookingIdError = validateBookingId(trimString(input.bookingId));
  if (bookingIdError) return bookingIdError;

  if (!trimString(input.newLockKey)) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.NEW_LOCK_KEY_REQUIRED;
  }

  const inviteeEmail = trimString(input.inviteeEmail);
  if (!inviteeEmail) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVITEE_EMAIL_REQUIRED;
  }
  if (!isValidEmail(inviteeEmail)) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_INVITEE_EMAIL;
  }

  if (trimString(input.reason).length > 500) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.REASON_TOO_LONG;
  }

  return null;
};

const validateCancelBookingInput = (
  input: SchedulePublicCancelBookingInput,
): string | null => {
  const publicKeyError = validatePublicKey(trimString(input.publicKey));
  if (publicKeyError) return publicKeyError;

  const bookingIdError = validateBookingId(trimString(input.bookingId));
  if (bookingIdError) return bookingIdError;

  const inviteeEmail = trimString(input.inviteeEmail);
  if (!inviteeEmail) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVITEE_EMAIL_REQUIRED;
  }
  if (!isValidEmail(inviteeEmail)) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_INVITEE_EMAIL;
  }

  if (trimString(input.reason).length > 500) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.REASON_TOO_LONG;
  }

  return null;
};

const validatePublicBookingDetailsInput = (
  input: GetSchedulePublicBookingDetailsInput,
): string | null => {
  const publicKeyError = validatePublicKey(trimString(input.publicKey));
  if (publicKeyError) return publicKeyError;

  const bookingIdError = validateBookingId(trimString(input.bookingId));
  if (bookingIdError) return bookingIdError;

  const inviteeEmail = trimString(input.inviteeEmail);
  if (!inviteeEmail) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVITEE_EMAIL_REQUIRED;
  }
  if (!isValidEmail(inviteeEmail)) {
    return SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_INVITEE_EMAIL;
  }

  return null;
};

const extractEnvelopeMessage = (value: unknown, fallback: string): string => {
  if (isPlainRecord(value)) {
    const message = value.message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return fallback;
};

const isWorkspace = (value: unknown): value is SchedulePublicWorkspace => {
  if (!isPlainRecord(value)) return false;
  return (
    isPositiveInteger(value.id) &&
    typeof value.name === "string" &&
    typeof value.slug === "string" &&
    typeof value.timezone === "string"
  );
};

const isHost = (value: unknown): value is SchedulePublicHost => {
  if (!isPlainRecord(value)) return false;
  return (
    isPositiveInteger(value.id) &&
    isPositiveInteger(value.userId) &&
    isPositiveInteger(value.availabilityScheduleId) &&
    typeof value.hostRole === "string" &&
    isNonNegativeInteger(value.priority) &&
    isPositiveInteger(value.weight) &&
    typeof value.isRequired === "boolean"
  );
};

const isLocation = (value: unknown): value is SchedulePublicLocation => {
  if (!isPlainRecord(value)) return false;
  return (
    isPositiveInteger(value.id) &&
    typeof value.locationType === "string" &&
    typeof value.isDefault === "boolean" &&
    isPositiveInteger(value.sortOrder)
  );
};

const isQuestion = (value: unknown): value is SchedulePublicQuestion => {
  if (!isPlainRecord(value)) return false;
  return (
    isPositiveInteger(value.id) &&
    typeof value.label === "string" &&
    typeof value.fieldKey === "string" &&
    typeof value.fieldType === "string" &&
    typeof value.isRequired === "boolean" &&
    isPositiveInteger(value.sortOrder)
  );
};

const isBookingLink = (value: unknown): value is SchedulePublicBookingLink => {
  if (!isPlainRecord(value)) return false;
  return (
    isPositiveInteger(value.eventTypeId) &&
    typeof value.publicKey === "string" &&
    typeof value.name === "string" &&
    typeof value.slug === "string" &&
    typeof value.eventTypeMode === "string" &&
    typeof value.durationMinutes === "number" &&
    typeof value.slotIntervalMinutes === "number" &&
    typeof value.timezoneMode === "string" &&
    typeof value.timezone === "string" &&
    typeof value.confirmationMode === "string" &&
    isWorkspace(value.workspace) &&
    Array.isArray(value.hosts) &&
    value.hosts.every(isHost) &&
    Array.isArray(value.locations) &&
    value.locations.every(isLocation) &&
    Array.isArray(value.questions) &&
    value.questions.every(isQuestion)
  );
};

const isAvailableSlot = (value: unknown): value is SchedulePublicAvailableSlot => {
  if (!isPlainRecord(value)) return false;
  return (
    typeof value.startTime === "string" &&
    typeof value.endTime === "string" &&
    typeof value.available === "boolean" &&
    Array.isArray(value.availableHostIds) &&
    value.availableHostIds.every((hostId) => typeof hostId === "number")
  );
};

const isAvailableSlotsData = (
  value: unknown,
): value is SchedulePublicAvailableSlotsData => {
  if (!isPlainRecord(value)) return false;
  return (
    isPositiveInteger(value.eventTypeId) &&
    typeof value.publicKey === "string" &&
    typeof value.date === "string" &&
    typeof value.timezone === "string" &&
    typeof value.durationMinutes === "number" &&
    typeof value.slotIntervalMinutes === "number" &&
    typeof value.eventTypeMode === "string" &&
    Array.isArray(value.slots) &&
    value.slots.every(isAvailableSlot)
  );
};

const isAvailableDatesData = (
  value: unknown,
): value is SchedulePublicAvailableDatesData => {
  if (!isPlainRecord(value)) return false;
  return (
    isPositiveInteger(value.eventTypeId) &&
    typeof value.publicKey === "string" &&
    typeof value.from === "string" &&
    typeof value.to === "string" &&
    typeof value.timezone === "string" &&
    Array.isArray(value.availableDates) &&
    value.availableDates.every((date) => typeof date === "string")
  );
};

const isReserveSlotData = (value: unknown): value is SchedulePublicReserveSlotData =>
  !value || isPlainRecord(value);

const isBookingData = (value: unknown): value is SchedulePublicBookingData =>
  !value || isPlainRecord(value);

const isEmailVerificationData = (
  value: unknown,
): value is SchedulePublicEmailVerificationData =>
  isPlainRecord(value) &&
  isNonNegativeInteger(value.verificationTokenId) &&
  (typeof value.expiresAt === "undefined" ||
    value.expiresAt === null ||
    typeof value.expiresAt === "string") &&
  (typeof value.expiresInMinutes === "undefined" ||
    value.expiresInMinutes === null ||
    typeof value.expiresInMinutes === "number");

const isBookingDetailsData = (
  value: unknown,
): value is SchedulePublicBookingDetails => isPlainRecord(value);

const extractBookingDetailsData = (
  value: unknown,
): SchedulePublicBookingDetails | null => {
  if (!isPlainRecord(value)) return null;

  const envelope = value as SchedulePublicBookingDetailsResponse;
  if (envelope.success && isBookingDetailsData(envelope.data)) {
    return envelope.data;
  }

  const nestedBooking = value.booking;
  if (isBookingDetailsData(nestedBooking)) {
    return nestedBooking;
  }

  return isBookingDetailsData(value) ? value : null;
};

const isRescheduleBookingData = (
  value: unknown,
): value is SchedulePublicRescheduleBookingData => isPlainRecord(value);

const isCancelBookingData = (
  value: unknown,
): value is SchedulePublicCancelBookingData => isPlainRecord(value);

const buildReserveSlotRequest = (
  input: SchedulePublicReserveSlotInput,
): SchedulePublicReserveSlotRequest => ({
  date: trimString(input.date),
  startTime: trimString(input.startTime),
  endTime: trimString(input.endTime),
  timezone: trimString(input.timezone),
  source: trimString(input.source) || "public_booking_page",
  medium: trimString(input.medium) || "web",
  campaign: trimString(input.campaign),
  inviteeEmail: trimString(input.inviteeEmail),
  ...(trimString(input.inviteeName)
    ? { inviteeName: trimString(input.inviteeName) }
    : {}),
  ...(trimString(input.inviteePhone)
    ? { inviteePhone: trimString(input.inviteePhone) }
    : {}),
  ...(trimString(input.inviteeTimezone)
    ? { inviteeTimezone: trimString(input.inviteeTimezone) }
    : {}),
  ...(typeof input.crmContactId === "number" && input.crmContactId > 0
    ? { crmContactId: input.crmContactId }
    : {}),
});

const buildCreateBookingRequest = (
  input: SchedulePublicCreateBookingInput,
): SchedulePublicCreateBookingInput => ({
  publicKey: trimString(input.publicKey),
  lockKey: trimString(input.lockKey),
  inviteeTimezone: trimString(input.inviteeTimezone),
  source: trimString(input.source) || "public_booking_page",
  medium: trimString(input.medium) || "web",
  campaign: trimString(input.campaign),
  bookingChannel: trimString(input.bookingChannel) || "public_booking_page",
  routingFormSubmissionId: input.routingFormSubmissionId ?? null,
  invitees: input.invitees.map((invitee) => ({
    name: trimString(invitee.name),
    email: trimString(invitee.email),
    phone: trimString(invitee.phone),
    timezone: trimString(invitee.timezone),
    crmContactId: invitee.crmContactId ?? null,
    metadata: invitee.metadata ?? {},
  })),
  answers: input.answers.map((answer) => ({
    questionId: answer.questionId,
    fieldKey: trimString(answer.fieldKey),
    answerValue: trimString(answer.answerValue),
    answerJson: answer.answerJson ?? {},
  })),
  ...(typeof input.verificationTokenId !== "undefined"
    ? { verificationTokenId: input.verificationTokenId }
    : {}),
  ...(trimString(input.verificationCode)
    ? { verificationCode: trimString(input.verificationCode) }
    : {}),
});

const buildSendEmailVerificationRequest = (
  input: SchedulePublicSendEmailVerificationInput,
): SchedulePublicSendEmailVerificationRequest => ({
  publicKey: trimString(input.publicKey),
  email: trimString(input.email),
});

const buildRescheduleBookingRequest = (
  input: SchedulePublicRescheduleBookingInput,
): SchedulePublicRescheduleBookingRequest => ({
  newLockKey: trimString(input.newLockKey),
  inviteeEmail: trimString(input.inviteeEmail),
  reason: trimString(input.reason),
  source: trimString(input.source) || "public_booking_page",
  medium: trimString(input.medium) || "web",
  campaign: trimString(input.campaign),
});

const buildCancelBookingRequest = (
  input: SchedulePublicCancelBookingInput,
): SchedulePublicCancelBookingRequest => ({
  inviteeEmail: trimString(input.inviteeEmail),
  reason: trimString(input.reason),
  source: trimString(input.source) || "public_booking_page",
  medium: trimString(input.medium) || "WEB",
  campaign: trimString(input.campaign),
});

export const schedulePublicApi = createApi({
  reducerPath: "schedulePublicApi",
  baseQuery: publicBaseQuery,
  tagTypes: [
    "SchedulePublicBookingLink",
    "SchedulePublicBookingDetails",
    "SchedulePublicAvailableSlots",
    "SchedulePublicAvailableDates",
  ],
  keepUnusedDataFor: 60,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getSchedulePublicBookingLink: builder.query<
      SchedulePublicBookingLink,
      GetSchedulePublicBookingLinkInput
    >({
      queryFn: async (input, _api, _extraOptions, baseQuery) => {
        const normalizedInput = normalizeBookingLinkInput(input);
        const publicKeyError = validatePublicKey(normalizedInput.publicKey);
        if (publicKeyError) return createApiError(400, publicKeyError);

        const result = await baseQuery(
          ENDPOINTS.bookingLinks.byPublicKey(normalizedInput.publicKey),
        );
        if (result.error) return { error: result.error };

        const envelope = result.data as SchedulePublicBookingLinkResponse;
        if (!envelope?.success || !isBookingLink(envelope.data)) {
          return createApiError(
            500,
            extractEnvelopeMessage(
              result.data,
              SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_RESPONSE,
            ),
          );
        }

        return { data: envelope.data };
      },
      providesTags: (result, _error, input) => {
        const publicKey =
          typeof input === "string" ? input : input.publicKey;
        return [
          { type: "SchedulePublicBookingLink", id: publicKey },
          ...(result
            ? [{ type: "SchedulePublicBookingLink" as const, id: result.eventTypeId }]
            : []),
        ];
      },
    }),

    getSchedulePublicBookingDetails: builder.query<
      SchedulePublicBookingDetails,
      GetSchedulePublicBookingDetailsInput
    >({
      queryFn: async (input, _api, _extraOptions, baseQuery) => {
        const validationError = validatePublicBookingDetailsInput(input);
        if (validationError) return createApiError(400, validationError);

        const queryParams = new URLSearchParams({
          inviteeEmail: trimString(input.inviteeEmail),
        });
        const result = await baseQuery(
          `${ENDPOINTS.bookingLinks.bookingDetails(
            trimString(input.publicKey),
            trimString(input.bookingId),
          )}?${queryParams.toString()}`,
        );
        if (result.error) return { error: result.error };

        const bookingDetails = extractBookingDetailsData(result.data);
        if (!bookingDetails) {
          return createApiError(
            500,
            extractEnvelopeMessage(
              result.data,
              SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_RESPONSE,
            ),
          );
        }

        return { data: bookingDetails };
      },
      providesTags: (_result, _error, input) => [
        {
          type: "SchedulePublicBookingDetails",
          id: `${input.publicKey}-${input.bookingId}-${input.inviteeEmail}`,
        },
      ],
    }),

    getSchedulePublicAvailableSlots: builder.query<
      SchedulePublicAvailableSlotsData,
      GetSchedulePublicAvailableSlotsInput
    >({
      queryFn: async (input, _api, _extraOptions, baseQuery) => {
        const validationError = validateSlotsInput(input);
        if (validationError) return createApiError(400, validationError);

        const queryParams = new URLSearchParams({
          date: trimString(input.date),
          timezone: trimString(input.timezone),
        });
        const result = await baseQuery(
          `${ENDPOINTS.bookingLinks.slots(
            trimString(input.publicKey),
          )}?${queryParams.toString()}`,
        );
        if (result.error) return { error: result.error };

        const envelope = result.data as SchedulePublicAvailableSlotsResponse;
        if (!envelope?.success || !isAvailableSlotsData(envelope.data)) {
          return createApiError(
            500,
            extractEnvelopeMessage(
              result.data,
              SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_RESPONSE,
            ),
          );
        }

        return { data: envelope.data };
      },
      providesTags: (_result, _error, input) => [
        {
          type: "SchedulePublicAvailableSlots",
          id: `${input.publicKey}-${input.date}-${input.timezone}`,
        },
      ],
    }),

    getSchedulePublicAvailableDates: builder.query<
      SchedulePublicAvailableDatesData,
      GetSchedulePublicAvailableDatesInput
    >({
      queryFn: async (input, _api, _extraOptions, baseQuery) => {
        const validationError = validateAvailableDatesInput(input);
        if (validationError) return createApiError(400, validationError);

        const queryParams = new URLSearchParams({
          from: trimString(input.from),
          to: trimString(input.to),
          timezone: trimString(input.timezone),
        });
        const result = await baseQuery(
          `${ENDPOINTS.bookingLinks.availabilityDates(
            trimString(input.publicKey),
          )}?${queryParams.toString()}`,
        );
        if (result.error) return { error: result.error };

        const envelope = result.data as SchedulePublicAvailableDatesResponse;
        if (!envelope?.success || !isAvailableDatesData(envelope.data)) {
          return createApiError(
            500,
            extractEnvelopeMessage(
              result.data,
              SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_RESPONSE,
            ),
          );
        }

        return { data: envelope.data };
      },
      providesTags: (_result, _error, input) => [
        {
          type: "SchedulePublicAvailableDates",
          id: `${input.publicKey}-${input.from}-${input.to}-${input.timezone}`,
        },
      ],
    }),

    reserveSchedulePublicSlot: builder.mutation<
      SchedulePublicReserveSlotData,
      SchedulePublicReserveSlotInput
    >({
      queryFn: async (input, _api, _extraOptions, baseQuery) => {
        const validationError = validateReserveSlotInput(input);
        if (validationError) return createApiError(400, validationError);

        const result = await baseQuery({
          url: ENDPOINTS.bookingLinks.reserveSlot(trimString(input.publicKey)),
          method: "POST",
          body: buildReserveSlotRequest(input),
        });
        if (result.error) return { error: result.error };

        const envelope = result.data as SchedulePublicReserveSlotResponse;
        if (!envelope?.success || !isReserveSlotData(envelope.data)) {
          return createApiError(
            500,
            extractEnvelopeMessage(
              result.data,
              SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_RESPONSE,
            ),
          );
        }

        return { data: envelope.data ?? {} };
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "SchedulePublicAvailableSlots",
          id: `${input.publicKey}-${input.date}-${input.timezone}`,
        },
      ],
    }),

    createSchedulePublicBooking: builder.mutation<
      SchedulePublicCreateBookingResult,
      SchedulePublicCreateBookingInput
    >({
      queryFn: async (input, _api, _extraOptions, baseQuery) => {
        const validationError = validateCreateBookingInput(input);
        if (validationError) return createApiError(400, validationError);

        const result = await baseQuery({
          url: ENDPOINTS.bookings.create,
          method: "POST",
          body: buildCreateBookingRequest(input),
        });
        if (result.error) return { error: result.error };

        const envelope = result.data as SchedulePublicCreateBookingResponse;
        if (!envelope?.success || !isBookingData(envelope.data)) {
          return createApiError(
            500,
            extractEnvelopeMessage(
              result.data,
              SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_RESPONSE,
            ),
          );
        }

        return {
          data: {
            booking: envelope.data ?? {},
            message:
              extractEnvelopeMessage(
                result.data,
                "Booking created successfully",
              ) || "Booking created successfully",
          },
        };
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "SchedulePublicAvailableSlots",
          id: `${input.publicKey}-${input.lockKey}-${input.inviteeTimezone}`,
        },
      ],
    }),

    sendSchedulePublicEmailVerification: builder.mutation<
      SchedulePublicEmailVerificationData,
      SchedulePublicSendEmailVerificationInput
    >({
      queryFn: async (input, _api, _extraOptions, baseQuery) => {
        const validationError = validateSendEmailVerificationInput(input);
        if (validationError) return createApiError(400, validationError);

        const result = await baseQuery({
          url: ENDPOINTS.bookings.sendEmailVerification,
          method: "POST",
          body: buildSendEmailVerificationRequest(input),
        });
        if (result.error) return { error: result.error };

        const envelope = result.data as SchedulePublicSendEmailVerificationResponse;
        if (!envelope?.success || !isEmailVerificationData(envelope.data)) {
          return createApiError(
            500,
            extractEnvelopeMessage(
              result.data,
              SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_RESPONSE,
            ),
          );
        }

        return { data: envelope.data };
      },
    }),

    rescheduleSchedulePublicBooking: builder.mutation<
      SchedulePublicRescheduleBookingResult,
      SchedulePublicRescheduleBookingInput
    >({
      queryFn: async (input, _api, _extraOptions, baseQuery) => {
        const validationError = validateRescheduleBookingInput(input);
        if (validationError) return createApiError(400, validationError);

        const result = await baseQuery({
          url: ENDPOINTS.bookingLinks.reschedule(
            trimString(input.publicKey),
            trimString(input.bookingId),
          ),
          method: "POST",
          body: buildRescheduleBookingRequest(input),
        });
        if (result.error) return { error: result.error };

        const envelope = result.data as SchedulePublicRescheduleBookingResponse;
        if (!envelope?.success || !isRescheduleBookingData(envelope.data)) {
          return createApiError(
            500,
            extractEnvelopeMessage(
              result.data,
              SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_RESPONSE,
            ),
          );
        }

        return {
          data: {
            booking: envelope.data,
            message:
              extractEnvelopeMessage(
                result.data,
                "Booking re-scheduled successfully",
              ) || "Booking re-scheduled successfully",
          },
        };
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "SchedulePublicAvailableSlots",
          id: `${input.publicKey}-${input.newLockKey}`,
        },
      ],
    }),

    cancelSchedulePublicBooking: builder.mutation<
      SchedulePublicCancelBookingResult,
      SchedulePublicCancelBookingInput
    >({
      queryFn: async (input, _api, _extraOptions, baseQuery) => {
        const validationError = validateCancelBookingInput(input);
        if (validationError) return createApiError(400, validationError);

        const result = await baseQuery({
          url: ENDPOINTS.bookingLinks.cancel(
            trimString(input.publicKey),
            trimString(input.bookingId),
          ),
          method: "POST",
          body: buildCancelBookingRequest(input),
        });
        if (result.error) return { error: result.error };

        const envelope = result.data as SchedulePublicCancelBookingResponse;
        if (!envelope?.success || !isCancelBookingData(envelope.data)) {
          return createApiError(
            500,
            extractEnvelopeMessage(
              result.data,
              SCHEDULE_PUBLIC_VALIDATION_MESSAGES.INVALID_RESPONSE,
            ),
          );
        }

        return {
          data: {
            booking: envelope.data,
            message:
              extractEnvelopeMessage(
                result.data,
                "Booking cancelled successfully",
              ) || "Booking cancelled successfully",
          },
        };
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "SchedulePublicBookingDetails",
          id: `${input.publicKey}-${input.bookingId}-${input.inviteeEmail}`,
        },
      ],
    }),
  }),
});

export const {
  useGetSchedulePublicBookingLinkQuery,
  useLazyGetSchedulePublicBookingLinkQuery,
  useGetSchedulePublicBookingDetailsQuery,
  useLazyGetSchedulePublicBookingDetailsQuery,
  useGetSchedulePublicAvailableSlotsQuery,
  useLazyGetSchedulePublicAvailableSlotsQuery,
  useGetSchedulePublicAvailableDatesQuery,
  useLazyGetSchedulePublicAvailableDatesQuery,
  useReserveSchedulePublicSlotMutation,
  useCreateSchedulePublicBookingMutation,
  useSendSchedulePublicEmailVerificationMutation,
  useRescheduleSchedulePublicBookingMutation,
  useCancelSchedulePublicBookingMutation,
} = schedulePublicApi;
